import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2 } from 'lucide-react';

// Signer colors for visual distinction (must match DocumentFieldEditorV2.js and FieldNodeV2.js)
const SIGNER_COLORS = [
  { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-700', light: 'bg-blue-100', hex: '#3B82F6' },
  { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-700', light: 'bg-green-100', hex: '#22C55E' },
  { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-700', light: 'bg-purple-100', hex: '#A855F7' },
  { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-700', light: 'bg-orange-100', hex: '#F97316' },
  { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-700', light: 'bg-pink-100', hex: '#EC4899' },
];

const PropertiesPanel = ({ recipients = [] }) => {
  const { fields, selectedFieldIds, updateField, deleteField, clearSelection, signers } = useEditorStore();

  // Determine if we have multiple signers
  const hasMultipleSigners = signers && signers.length > 1;
  
  const selectedField = selectedFieldIds.length === 1
    ? fields.find((f) => f.id === selectedFieldIds[0])
    : null;
  
  if (!selectedField) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Properties</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Select a field to edit its properties</p>
        </CardContent>
      </Card>
    );
  }
  
  const handleUpdateProp = (key, value) => {
    updateField(selectedField.id, {
      props: { ...selectedField.props, [key]: value },
    });
  };
  
  const handleDelete = () => {
    deleteField(selectedField.id);
    clearSelection();
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center justify-between">
          <span>Properties</span>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Field Type */}
        <div>
          <Label className="text-xs text-gray-500">Type</Label>
          <p className="text-sm font-medium capitalize">{selectedField.type}</p>
        </div>
        
        {/* Assign to Recipient - Enhanced with signer colors when multiple signers */}
        {recipients.length > 0 && (
          <div>
            <Label className="text-xs">Assign to</Label>
            {hasMultipleSigners ? (
              // Multi-signer mode: show colored signer buttons instead of dropdown
              <div className="mt-2 space-y-2">
                {signers.map((signer, index) => {
                  const color = SIGNER_COLORS[index % SIGNER_COLORS.length];
                  // Match by signerId (e.g., "signer_1") instead of email
                  const isAssigned = selectedField.props?.assignedTo === signer.signerId;
                  const signerLabel = signer.name || `Signer ${index + 1}`;

                  return (
                    <button
                      key={index}
                      onClick={() => handleUpdateProp('assignedTo', signer.signerId)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isAssigned
                          ? `${color.bg} text-white shadow-md`
                          : `${color.light} ${color.text} hover:opacity-80 border ${color.border}`
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full ${isAssigned ? 'bg-white' : color.bg}`}></span>
                      <span className="flex-1 text-left">Signer {index + 1}</span>
                      <span className="text-xs opacity-75 truncate max-w-[100px]">{signerLabel}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              // Single recipient mode: use standard dropdown
              <select
                value={selectedField.props?.assignedTo || ''}
                onChange={(e) => handleUpdateProp('assignedTo', e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Everyone</option>
                {recipients.map((recipient, index) => (
                  <option key={index} value={recipient.email}>
                    {recipient.name} ({recipient.email})
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {hasMultipleSigners ? (
                selectedField.props?.assignedTo
                  ? (() => {
                      const signerIdx = signers.findIndex(s => s.email === selectedField.props?.assignedTo);
                      const signerName = signers[signerIdx]?.name || `Signer ${signerIdx + 1}`;
                      return `✓ Assigned to ${signerName}. Only they will see this field.`;
                    })()
                  : '⚠️ No signer selected. Please assign this field to a signer.'
              ) : (
                selectedField.props?.assignedTo
                  ? `Only ${recipients.find(r => r.email === selectedField.props?.assignedTo)?.name || 'this recipient'} will see this field`
                  : recipients.length >= 2
                    ? '⚠️ All recipients will see this field. For agreements, assign fields to specific recipients.'
                    : 'All recipients will see this field'
              )}
            </p>
          </div>
        )}
        
        {/* Label */}
        <div>
          <Label className="text-xs">Label</Label>
          <Input
            value={selectedField.props?.label || ''}
            onChange={(e) => handleUpdateProp('label', e.target.value)}
            placeholder="Field label"
            className="mt-1"
          />
        </div>
        
        {/* Required */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="required"
            checked={selectedField.props?.required || false}
            onChange={(e) => handleUpdateProp('required', e.target.checked)}
            className="rounded"
          />
          <Label htmlFor="required" className="text-xs cursor-pointer">
            Required field
          </Label>
        </div>
        
        {/* Type-specific props */}
        {selectedField.type === 'text' && (
          <>
            <div>
              <Label className="text-xs">Placeholder</Label>
              <Input
                value={selectedField.props?.placeholder || ''}
                onChange={(e) => handleUpdateProp('placeholder', e.target.value)}
                placeholder="Enter text..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max Length</Label>
              <Input
                type="number"
                value={selectedField.props?.maxLength || 100}
                onChange={(e) => handleUpdateProp('maxLength', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
          </>
        )}
        
        {selectedField.type === 'date' && (
          <div>
            <Label className="text-xs">Date Format</Label>
            <select
              value={selectedField.props?.dateFormat || 'YYYY-MM-DD'}
              onChange={(e) => handleUpdateProp('dateFormat', e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
              <option value="YYYY/MM/DD">YYYY/MM/DD (2024/12/31)</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY (31-12-2024)</option>
              <option value="MM-DD-YYYY">MM-DD-YYYY (12-31-2024)</option>
            </select>
          </div>
        )}
        
        {/* Name Field */}
        {selectedField.type === 'name' && (
          <div>
            <Label className="text-xs">Placeholder</Label>
            <Input
              value={selectedField.props?.placeholder || ''}
              onChange={(e) => handleUpdateProp('placeholder', e.target.value)}
              placeholder="Enter your name"
              className="mt-1"
            />
          </div>
        )}
        
        {/* Email Field */}
        {selectedField.type === 'email' && (
          <div>
            <Label className="text-xs">Placeholder</Label>
            <Input
              value={selectedField.props?.placeholder || ''}
              onChange={(e) => handleUpdateProp('placeholder', e.target.value)}
              placeholder="Enter your email"
              className="mt-1"
            />
          </div>
        )}
        
        {/* Initials Field */}
        {selectedField.type === 'initials' && (
          <div>
            <Label className="text-xs text-gray-500">Type</Label>
            <p className="text-xs text-gray-600 mt-1">Signers will draw their initials in a smaller signature pad</p>
          </div>
        )}
        
        {/* Image Field */}
        {selectedField.type === 'image' && (
          <>
            <div>
              <Label className="text-xs">Max File Size</Label>
              <select
                value={selectedField.props?.maxSize || '2MB'}
                onChange={(e) => handleUpdateProp('maxSize', e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="1MB">1 MB</option>
                <option value="2MB">2 MB</option>
                <option value="5MB">5 MB</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Accepted Formats</Label>
              <p className="text-xs text-gray-600 mt-1">PNG, JPG, JPEG</p>
            </div>
          </>
        )}
        
        {/* Radio Group Options */}
        {selectedField.type === 'radio' && (
          <>
            <div>
              <Label className="text-xs">Layout</Label>
              <select
                value={selectedField.props?.layout || 'vertical'}
                onChange={(e) => {
                  const newLayout = e.target.value;
                  const currentOptions = selectedField.props?.options || [];
                  
                  // Reposition options based on new layout
                  const repositionedOptions = currentOptions.map((opt, index) => {
                    if (newLayout === 'vertical') {
                      // Stack vertically with 15% spacing
                      return {
                        ...opt,
                        x: 0.1,
                        y: 0.1 + (index * 0.25)
                      };
                    } else {
                      // Arrange horizontally with spacing
                      return {
                        ...opt,
                        x: 0.1 + (index * 0.3),
                        y: 0.5
                      };
                    }
                  });
                  
                  updateField(selectedField.id, {
                    props: {
                      ...selectedField.props,
                      layout: newLayout,
                      options: repositionedOptions
                    }
                  });
                }}
                className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>
            
            <div>
              <Label className="text-xs">Spacing (px)</Label>
              <Input
                type="number"
                value={selectedField.props?.spacingPx || 8}
                onChange={(e) => handleUpdateProp('spacingPx', parseInt(e.target.value))}
                min={0}
                max={20}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold mb-2 block">Options</Label>
              {(selectedField.props?.options || []).map((option, index) => (
                <div key={option.id} className="flex gap-2 mb-2 items-center">
                  <Input
                    value={option.label}
                    onChange={(e) => {
                      const newOptions = [...(selectedField.props?.options || [])];
                      newOptions[index] = { ...option, label: e.target.value };
                      handleUpdateProp('options', newOptions);
                    }}
                    placeholder="Option label"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentOptions = selectedField.props?.options || [];
                      const newOptions = currentOptions.filter((_, i) => i !== index);
                      // If all options are removed, delete the field
                      if (newOptions.length === 0) {
                        deleteField(selectedField.id);
                        clearSelection();
                      } else {
                        handleUpdateProp('options', newOptions);
                      }
                    }}
                    className="text-red-600"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentOptions = selectedField.props?.options || [];
                  const newId = String(currentOptions.length + 1);
                  const layout = selectedField.props?.layout || 'vertical';
                  const index = currentOptions.length;
                  
                  // Calculate default position based on layout
                  let defaultX, defaultY;
                  if (layout === 'vertical') {
                    defaultX = 0.1;
                    defaultY = 0.1 + (index * 0.25); // 25% spacing
                  } else {
                    defaultX = 0.1 + (index * 0.3); // 30% spacing
                    defaultY = 0.5;
                  }
                  
                  const newOptions = [
                    ...currentOptions,
                    { 
                      id: newId, 
                      label: '', // Empty label by default (SignEasy style)
                      value: `option${newId}`,
                      x: defaultX,
                      y: defaultY
                    }
                  ];
                  handleUpdateProp('options', newOptions);
                }}
                className="w-full mt-1"
              >
                + Add Option
              </Button>
            </div>
          </>
        )}
        
        {/* Checkbox Group Options (only for checkbox-group, not single checkbox) */}
        {selectedField.type === 'checkbox-group' && (
          <>
            <div>
              <Label className="text-xs">Layout</Label>
              <select
                value={selectedField.props?.layout || 'vertical'}
                onChange={(e) => {
                  const newLayout = e.target.value;
                  const currentOptions = selectedField.props?.options || [];
                  
                  // Reposition options based on new layout
                  const repositionedOptions = currentOptions.map((opt, index) => {
                    if (newLayout === 'vertical') {
                      // Stack vertically with spacing
                      return {
                        ...opt,
                        x: 0.1,
                        y: 0.1 + (index * 0.25)
                      };
                    } else {
                      // Arrange horizontally with spacing
                      return {
                        ...opt,
                        x: 0.1 + (index * 0.3),
                        y: 0.5
                      };
                    }
                  });
                  
                  updateField(selectedField.id, {
                    props: {
                      ...selectedField.props,
                      layout: newLayout,
                      options: repositionedOptions
                    }
                  });
                }}
                className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>
            
            <div>
              <Label className="text-xs">Spacing (px)</Label>
              <Input
                type="number"
                value={selectedField.props?.spacingPx || 8}
                onChange={(e) => handleUpdateProp('spacingPx', parseInt(e.target.value))}
                min={0}
                max={20}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold mb-2 block">Options</Label>
              {(selectedField.props?.options || []).map((option, index) => (
                <div key={option.id} className="flex gap-2 mb-2 items-center">
                  <Input
                    value={option.label}
                    onChange={(e) => {
                      const newOptions = [...(selectedField.props?.options || [])];
                      newOptions[index] = { ...option, label: e.target.value };
                      handleUpdateProp('options', newOptions);
                    }}
                    placeholder="Option label"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentOptions = selectedField.props?.options || [];
                      const newOptions = currentOptions.filter((_, i) => i !== index);
                      // If all options are removed, delete the field
                      if (newOptions.length === 0) {
                        deleteField(selectedField.id);
                        clearSelection();
                      } else {
                        handleUpdateProp('options', newOptions);
                      }
                    }}
                    className="text-red-600"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentOptions = selectedField.props?.options || [];
                  const newId = String(currentOptions.length + 1);
                  const layout = selectedField.props?.layout || 'vertical';
                  const index = currentOptions.length;
                  
                  // Calculate default position based on layout
                  let defaultX, defaultY;
                  if (layout === 'vertical') {
                    defaultX = 0.1;
                    defaultY = 0.1 + (index * 0.25); // 25% spacing
                  } else {
                    defaultX = 0.1 + (index * 0.3); // 30% spacing
                    defaultY = 0.5;
                  }
                  
                  const newOptions = [
                    ...currentOptions,
                    { 
                      id: newId, 
                      label: '', // Empty label by default (SignEasy style)
                      value: `option${newId}`,
                      x: defaultX,
                      y: defaultY
                    }
                  ];
                  handleUpdateProp('options', newOptions);
                }}
                className="w-full mt-1"
              >
                + Add Option
              </Button>
            </div>
          </>
        )}
        
        {/* Position & Size (read-only) */}
        <div className="pt-2 border-t border-gray-200">
          <Label className="text-xs text-gray-500">Position</Label>
          <div className="grid grid-cols-2 gap-2 mt-1 text-xs text-gray-600">
            <div>X: {(selectedField.x * 100).toFixed(1)}%</div>
            <div>Y: {(selectedField.y * 100).toFixed(1)}%</div>
            <div>W: {(selectedField.w * 100).toFixed(1)}%</div>
            <div>H: {(selectedField.h * 100).toFixed(1)}%</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertiesPanel;